import { useState } from "react";
import type { Deal } from "../types";
import { calc } from "../engine/engine";
import { project, HORIZONS } from "../lib/projection";
import { money0, pct, x2 } from "../lib/format";
import { LineChart } from "./Charts";

const C = { value: "#17352B", equity: "#3E7A5E", debt: "#A6503C" };

function Kpi({ label, value, sub, good }: { label: string; value: string; sub?: string; good?: boolean }) {
  return (
    <div className="card kpi">
      <div className="kl">{label}</div>
      <div className="kv">{value}</div>
      {sub && <div className={"ks" + (good ? " good" : "")}>{sub}</div>}
    </div>
  );
}

export function Dashboard({ deals: allDeals, onOpen }: { deals: Deal[]; onOpen: (id: string) => void }) {
  const [horizon, setHorizon] = useState<number>(5);
  const [bump, setBump] = useState<number>(50);
  const deals = allDeals.filter((d) => d.kind !== "land-development");
  const developmentDeals = allDeals.filter((d) => d.kind === "land-development" && d.development);

  const rows = deals.map((d) => {
    const m = calc(d);
    return { d, m, p: project(d, m, horizon, bump) };
  });

  // portfolio totals today
  const units = deals.reduce((s, d) => s + d.u2c + d.u1c, 0);
  const valueNow = rows.reduce((s, r) => s + r.p.v0, 0);
  const debtNow = rows.reduce((s, r) => s + r.p.l0, 0);
  const equityNow = valueNow - debtNow;
  const cashYr = rows.reduce((s, r) => s + r.m.cashFlow, 0);

  // summed projection over the horizon
  const years = rows[0]?.p.rows.map((r) => r.year) ?? [];
  const sumAt = (fn: (i: number) => number) => years.map((_, i) => fn(i));
  const pValue = sumAt((i) => rows.reduce((s, r) => s + r.p.rows[i].value, 0));
  const pEquity = sumAt((i) => rows.reduce((s, r) => s + r.p.rows[i].equity, 0));
  const pDebt = sumAt((i) => rows.reduce((s, r) => s + r.p.rows[i].loanBalance, 0));

  const endEquity = pEquity[pEquity.length - 1] ?? equityNow;
  const totalCashOut = rows.reduce((s, r) => s + r.p.cashOutAtEnd, 0);
  const nextBuyingPower = totalCashOut / 0.25;

  return (
    <div className="wrap">
      <div className="pe">
        <div className="l">◆ Administrator dashboard</div>
        <p>
          Your whole book, projected forward. See net worth compound as tenants pay down your loans, the market
          appreciates, and you force value by raising rents — then how much you can <b>refinance out to buy the next
          property</b>. Adjust the horizon and the value-add lever to simulate.
        </p>
      </div>

      {/* controls */}
      <div className="card pad" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Horizon</div>
            <div className="depthbar" style={{ border: "1px solid var(--line)", borderRadius: 100 }}>
              {HORIZONS.map((h) => (
                <button key={h} className={horizon === h ? "on" : ""} style={{ color: horizon === h ? "#241a08" : "var(--forest)", padding: "6px 16px" }} onClick={() => setHorizon(h)}>{h} yr</button>
              ))}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Value-add across the portfolio <span className="mono" style={{ color: "var(--brass-deep)" }}>+{money0(bump)}/unit/mo</span></div>
            <input type="range" min={0} max={200} step={5} value={bump} onChange={(e) => setBump(+e.target.value)} style={{ width: "100%", accentColor: "var(--brass)" }} />
          </div>
        </div>
      </div>

      {/* today's book */}
      <div className="grid g4">
        <Kpi label="Properties" value={String(allDeals.length)} sub={`${deals.length} income + ${developmentDeals.length} development`} />
        <Kpi label="Portfolio value" value={money0(valueNow)} sub="stabilized, today" />
        <Kpi label="Your equity" value={money0(equityNow)} sub={money0(debtNow) + " debt"} good />
        <Kpi label="Cash flow / yr" value={money0(cashYr)} sub={money0(cashYr / 12) + "/mo" } good />
      </div>

      {developmentDeals.length > 0 && <>
        <h2 className="sechead" style={{ fontSize: 19 }}>Development feasibility</h2>
        <div className="grid g3">
          {developmentDeals.map((d) => <div key={d.id} className="card deal-card" style={{ padding: 16, cursor: "pointer" }} onClick={() => onOpen(d.id)}>
            <div className="fr" style={{ fontWeight: 600, fontSize: 18, color: "var(--forest)" }}>{d.name}</div>
            <div style={{ fontSize: 11.5, color: "var(--muted)", margin: "3px 0 10px" }}>{d.addr}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span className="tag t-watch">FEASIBILITY</span>
              <span className="tag" style={{ background: "var(--cream-200)", color: "var(--forest)" }}>{d.development!.grossAcres.toFixed(2)} acres</span>
              <span className="tag" style={{ background: "var(--cream-200)", color: "var(--forest)" }}>{d.development!.scenarios[0]?.units} base units</span>
            </div>
            <div className="note" style={{ marginTop: 10 }}>Excluded from stabilized portfolio and refinance totals until the development plan is entitled and financeable.</div>
          </div>)}
        </div>
      </>}

      {/* projected net worth */}
      <h2 className="sechead" style={{ fontSize: 19 }}>Projected portfolio net worth — {horizon} years</h2>
      <div className="card pad">
        <LineChart
          years={years}
          series={[
            { name: "Portfolio value", color: C.value, values: pValue },
            { name: "Your equity (net worth)", color: C.equity, values: pEquity, fill: true },
            { name: "Debt", color: C.debt, values: pDebt, dashed: true },
          ]}
          height={280}
        />
        <div className="note">Your equity grows from <b>{money0(equityNow)}</b> today to <b>{money0(endEquity)}</b> in {horizon} years — a <b>{money0(endEquity - equityNow)}</b> increase.</div>
      </div>

      {/* snowball */}
      <h2 className="sechead" style={{ fontSize: 19 }}>Refi snowball → next acquisitions</h2>
      <div className="card pad" style={{ borderLeft: "4px solid var(--brass)" }}>
        <div className="grid g3">
          <Kpi label={`Pullable equity in ${horizon} yr`} value={money0(totalCashOut)} sub="across all properties" good />
          <Kpi label="Funds next purchases of" value={money0(nextBuyingPower)} sub="at 25% down" good />
          <Kpi label="≈ additional doors" value={String(Math.max(0, Math.round(nextBuyingPower / (valueNow / Math.max(1, units)))))} sub="at today's price/unit" good />
        </div>
        <div className="flag ok" style={{ marginTop: 10 }}>
          <span>✓</span>
          <div>In {horizon} year{horizon > 1 ? "s" : ""}, refinancing the portfolio frees roughly <b>{money0(totalCashOut)}</b> — the down payment on about <b>{money0(nextBuyingPower)}</b> of new property. That's how one becomes three.</div>
        </div>
      </div>

      {/* per-property table */}
      <h2 className="sechead" style={{ fontSize: 19 }}>Every property</h2>
      <div className="card pad">
        <div className="tablewrap">
          <table className="t">
            <thead>
              <tr>
                <th>Property</th><th className="n">Value</th><th className="n">Equity</th><th className="n">Cash/yr</th>
                <th className="n">Cash-out ({horizon}yr)</th><th className="n">DSCR</th><th>Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ d, m, p }) => (
                <tr key={d.id} className="deal-card" style={{ cursor: "pointer" }} onClick={() => onOpen(d.id)}>
                  <td><b style={{ color: "var(--forest)" }}>{d.name}</b><div style={{ fontSize: 11, color: "var(--muted)" }}>{d.market}</div></td>
                  <td className="n">{money0(p.v0)}</td>
                  <td className="n">{money0(p.e0)}</td>
                  <td className="n">{money0(m.cashFlow)}</td>
                  <td className="n" style={{ color: "var(--pass)", fontWeight: 600 }}>{money0(p.cashOutAtEnd)}</td>
                  <td className="n">{x2(m.dscr)}</td>
                  <td><span className={"tag t-" + (m.score.cls === "pass" ? "pass" : m.score.cls === "watch" ? "watch" : "fail")}>{m.score.gated ? "GATED" : m.score.composite}</span></td>
                </tr>
              ))}
              <tr className="tot">
                <td>Portfolio</td>
                <td className="n">{money0(valueNow)}</td>
                <td className="n">{money0(equityNow)}</td>
                <td className="n">{money0(cashYr)}</td>
                <td className="n">{money0(totalCashOut)}</td>
                <td className="n">—</td>
                <td>{pct(rows.reduce((s, r) => s + (r.m.score.gated ? 0 : r.m.score.composite), 0) / Math.max(1, deals.length) / 100, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
